import { pdf } from "@react-pdf/renderer";
import { httpClient } from "infra/axios/axios-api";
import { DigitalSign } from "domain/entities/digital-sign";
import { Tripsheet } from "domain/entities/tripsheet";
import { DigitalSignStatusEnum } from "domain/enum/digitalSignStatus";
import { TripsheetStatusEnum } from "domain/enum/tripsheetStatus";
import { TripsheetInputFilter } from "domain/models/tripsheet-input-filter";
import {
  translateRegexMessage,
  translateText,
} from "infra/translation/translate";
import Spinner from "presentation/components/Shared/Spinner";
import { useCreateDigitalSignMutation } from "presentation/hooks/use-query-digital-sign";
import { useDigitalSignApproverByFilter } from "presentation/hooks/use-query-digital-sign-approver";
import {
  useTripsheetsByFilter,
  useUpdateTripsheetMutation,
} from "presentation/hooks/use-query-tripsheet";
import { useTripsheetReport } from "presentation/hooks/use-query-tripsheet-report";
import { useFilterTripsheetStore } from "presentation/stores/use-filter-tripsheet-store";
import { useTripsheetDateFilterStore } from "presentation/stores/use-tripsheets-date-filter-store";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import {
  DataTable,
  DataTableFilterEvent,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import HeaderTripMonthEmployeeGrid from "./HeaderTripMonthEmployeeGrid";
import StatusTripsheetDropdown from "./StatusTripsheetDropdown";
import TripsheetMonthForm from "./TripsheetMonthForm";
import TripsheetReportPDF from "./TripsheetReportPDF";
import { DigitalSignApprover } from "domain/entities/digital-sign-approver";
import { DigitalSignStepEnum } from "domain/enum/digitalSignSteps";
import { MessageTypeEnum } from "domain/enum/messageType";
import PdfViewerDialog from "presentation/components/Shared/PdfViewerDialog";
import { useTimesheetsByFilter } from "presentation/hooks/use-query-timesheet";
import { TimesheetInputFilter } from "domain/models/timesheet-input-filter";
import { EMAIL } from "domain/enum/email";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { Timesheet } from "domain/entities/timesheet";

enum ActionTypeEnum {
  REPORT = "report",
  TO_SIGN = "toSign",
  SIGN = "sign",
  NONE = "none",
}

const TripsheetMonthGrid: React.FC = () => {
  const confirmAction = (message: string, acceptCallback: () => void) => {
    confirmDialog({
      message: message,
      header: "Confirmação",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sim",
      rejectLabel: "Não",
      accept: acceptCallback,
    });
  };

  const { email: userEmployeeEmail, userPermissions } = useAuthStore();

  const { filters, setFilters } = useFilterTripsheetStore();
  const toast = useRef<Toast>(null);
  const [showModal, setShowModal] = useState(false);
  const [s3PathToDisplay, setS3PathToDisplay] = useState<string | null>(null);
  const [tripsheetToAnalyse, setTripsheetToAnalyse] = useState<Tripsheet>();
  const [tripsheetIdToReport, setTripsheetIdToReport] = useState("");
  const { tripsheetDateFilter, setTripsheetDateFilter } =
    useTripsheetDateFilterStore();
  const [action, setAction] = useState<ActionTypeEnum>(ActionTypeEnum.NONE);

  const { data: digitalSignApproverData, refetch: refetchDigitalSignApprover } =
    useDigitalSignApproverByFilter({
      referenceId: tripsheetIdToReport,
    });

  const [lazyParams, setLazyParams] = useState({
    sortBy: "employee.employeeNumber",
    sortOrder: "asc",
    limit: 50,
    page: 1,
    filters: {},
  });

  const {
    data: tripsheetsData,
    isError,
    isLoading,
    error,
    refetch: refetchExistentTripsheets,
  } = useTripsheetsByFilter({
    year: tripsheetDateFilter?.date.getFullYear(),
    month: tripsheetDateFilter?.date.getMonth() + 1,
    sortBy: lazyParams.sortBy,
    sortOrder: lazyParams.sortOrder,
    page: lazyParams.page,
    limit: lazyParams.limit,
    ...lazyParams.filters,
  } as TripsheetInputFilter);

   const allowedTimesheetStatusToBlockTimesheet = ["Enviado"]
  const [employeeIdToCheckTimesheets, setEmployeeIdToCheckTimesheets] = useState("");
  const [monthToCheckTimesheets, setMonthToCheckTimesheets] = useState(0);
  const [yearToCheckTimesheets, setYearToCheckTimesheets] = useState(0);
  const [pendingTripsheet, setPendingTripsheet] = useState<Tripsheet | null>(null);

  const {
    data: timeheets,
    isLoading: isLoadingTimeheets,
    isError: isErrorLoadingTimeheets,
    error: errorLoadingTimeheets,
    refetch: refetchTimeheets,
  } = useTimesheetsByFilter({
    employeeId: employeeIdToCheckTimesheets,
    month: monthToCheckTimesheets,
    year: yearToCheckTimesheets,
    sortBy: "version",
    sortOrder: "desc"
  } as TimesheetInputFilter);

  const { data: tripsheetReportData, refetch: refetchReportData } =
    useTripsheetReport(tripsheetIdToReport, {
      sortBy: "dateTrip",
      page: 1,
      limit: 100,
    });

  const {
    mutateAsync: updateTripsheetMutation,
    error: updatingTripsheetError,
    isError: isUpdatingTripsheetError,
    isSuccess: isUpdatingTripsheetSuccess,
  } = useUpdateTripsheetMutation();

  const handleCloseModal = async () => {
    setShowModal(false);
    await refetchExistentTripsheets();
  };

  const { mutate: createDigitalSign, isSuccess: isSuccessCreateDigitalSign } =
    useCreateDigitalSignMutation();

  const showMessage = (
    type: MessageTypeEnum,
    subject: string,
    message: string
  ) => {
    toast.current?.show({
      severity: type,
      summary: subject,
      detail: message,
      life: 1500,
    });
  };

  useEffect(() => {
    if (!pendingTripsheet) return;

    const fn = async () => {
      const { data: refetched } = await refetchTimeheets();

      const maxVersionTimesheet = refetched?.data?.reduce<Timesheet | null>(
        (max, curr) => {
          if (!max || curr.version > max.version) {
            return curr;
          }
          return max;
        },
        null
      );

      if (!allowedTimesheetStatusToBlockTimesheet.includes(maxVersionTimesheet?.status ?? "")) {
        toast.current?.show({
          severity: "warn",
          summary: "Aviso",
          detail:
            "O Mapa de Kms não pode ser bloqueado pois o mapa de horas deste mês ainda não se encontra Enviado.",
          life: 10000,
        });
      } else {
        pendingTripsheet.lastModifiedBy = userEmployeeEmail ?? EMAIL;
        pendingTripsheet.status = TripsheetStatusEnum.BLOQUEADO;
        updateTripsheetMutation(pendingTripsheet);
      }
      setPendingTripsheet(null);
    };

    fn();
  }, [
    employeeIdToCheckTimesheets,
    monthToCheckTimesheets,
    yearToCheckTimesheets,
  ]);

  useEffect(() => {
    const fn = async () => { };
    fn();
  }, [digitalSignApproverData?.data]);

  useEffect(() => {
    setLazyParams((prev) => ({
      ...prev,
      filters: Object.fromEntries(
        Object.entries(filters)
          .map(([key, value]) => {
            if (key === "employee.name") key = "employeeName";
            else if (key === "employee.employeeNumber") key = "employeeNumber";
            if ("constraints" in value) {
              return [key, value.constraints?.[0]?.value ?? ""];
            }
            return [key, (value as DataTableFilterMetaData)?.value ?? ""];
          })
          .filter(
            ([_, value]) =>
              value !== null && value !== undefined && value !== ""
          )
      ),
    }));
  }, [filters]);

  useEffect(() => {
    refetchExistentTripsheets();
  }, [lazyParams.filters, refetchExistentTripsheets]);

  useEffect(() => {
    if (
      tripsheetIdToReport &&
      (action === ActionTypeEnum.REPORT || action === ActionTypeEnum.TO_SIGN)
    ) {
      refetchReportData();
    }
    if (tripsheetIdToReport && action === ActionTypeEnum.SIGN) {
      refetchDigitalSignApprover().then(async (result) => {
        if (result.data?.data) {
          // console.log("Digital Sign Approver Data:", result.data.data);
          await getSignedTripsheet(result.data.data);
        }
      });
    }
  }, [tripsheetIdToReport]);

  useEffect(() => {
    const generatePdf = async () => {
      if (!tripsheetReportData?.data) {
        // console.error("Dados do relatório não disponíveis");
        return;
      }

      try {
        const blob = await pdf(
          <TripsheetReportPDF data={tripsheetReportData.data} />
        ).toBlob();

        setTripsheetIdToReport("");
        return blob;
      } catch (error) {
        console.error("Erro ao gerar o PDF:", error);
        return undefined;
      }
    };

    if (tripsheetReportData?.data && action === ActionTypeEnum.REPORT) {
      const result = generatePdf();
      // console.log("ENTROU REPORT");
      result.then((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          window.open(url);
        }
      });
    } else if (tripsheetReportData?.data && action === ActionTypeEnum.TO_SIGN) {
      const result = generatePdf();
      result.then(async (blob) => {
        if (blob) {
          let url = "";

          if (blob) {
            const fileName = `relatorio-tripsheet-${formatName(
              tripsheetReportData.data?.employee.name ?? "ni"
            )}-${formatDate(
              tripsheetReportData.data?.year ?? new Date().getFullYear(),
              tripsheetReportData.data?.month ?? new Date().getMonth() + 1
            )}-${new Date().getTime()}.pdf`;

            const file = new File([blob], fileName, {
              type: "application/pdf",
            });

            const formData = new FormData();
            formData.append("file", file);

            try {
              const response = await httpClient.post(
                "/assets/tripsheets",
                formData
              );
              url = response.data.filePath;

              // Agora, passamos apenas o s3Path para o PdfViewerDialog
              setS3PathToDisplay(url);
            } catch (err) {
              console.error("Erro ao fazer upload do PDF:", err);
              return;
            }
          }

          if (url && tripsheetToAnalyse?.id) {
            tripsheetToAnalyse.status = "A Assinar";
            await updateTripsheetMutation(tripsheetToAnalyse).then(async () => {
              const eSign: DigitalSign = {
                documentType: "Tripsheet",
                referenceId: tripsheetToAnalyse.id ?? "",
                createdAt: new Date(),
                documentPath: url,
                status: DigitalSignStatusEnum.A_ASSINAR,
              };
              createDigitalSign(eSign);
              await refetchExistentTripsheets();
              await refetchReportData();
            });
          }
        }
      });
    }
    setAction(ActionTypeEnum.NONE);
  }, [tripsheetReportData]);

  useEffect(() => {
    if (
      isUpdatingTripsheetSuccess &&
      tripsheetToAnalyse?.status !== DigitalSignStatusEnum.A_ASSINAR
    ) {
      const successMessage = "Estado do mapa de km's atualizado com sucesso";
      showMessage(MessageTypeEnum.SUCCESS, "Sucesso", successMessage);
    }
  }, [isUpdatingTripsheetSuccess]);

  useEffect(() => {
    if (isSuccessCreateDigitalSign) {
      const successMessage = "Relatório de Viagem enviado para assinatura";
      showMessage(MessageTypeEnum.SUCCESS, "Sucesso", successMessage);
    }
  }, [isSuccessCreateDigitalSign]);

  useEffect(() => {
    if (isUpdatingTripsheetError) {
      let message = [];
      message.push(updatingTripsheetError.message);
      message = updatingTripsheetError.response.data.metadata.errors;
      message.map((item: string) => {
        showMessage(
          MessageTypeEnum.ERROR,
          "Erro",
          item || "Ocorreu um erro inesperado"
        );
      });
    }
  }, [updatingTripsheetError, isUpdatingTripsheetError]);

  if (isLoading) return <Spinner />;

  if (isError && toast.current) {
    showMessage(
      MessageTypeEnum.ERROR,
      "Mapa de KM",
      translateRegexMessage(error?.message, "pt") ||
      "Ocorreu um erro inesperado"
    );
  }

  const formatDate = (year: number, month: number): string => {
    const paddedMonth = String(month).padStart(2, "0");
    return `${year}${paddedMonth}`;
  };

  const formatName = (fullName: string) => {
    if (!fullName || typeof fullName !== "string") return "";

    const part = fullName.trim().split(/\s+/);
    if (part.length === 0) return "";

    const first = part[0];
    const last = part.length > 1 ? part[part.length - 1] : "";
    const shortName = [first, last].filter(Boolean).join("-");

    return shortName.toLowerCase();
  };

  const handlerToSign = async (rowData: Tripsheet) => {
    setAction(ActionTypeEnum.TO_SIGN);
    setTripsheetToAnalyse(rowData);

    if (rowData.id === tripsheetIdToReport) {
      await refetchReportData();
    } else if (rowData.id) {
      setTripsheetIdToReport(rowData.id);
    }
  };

  const showActionOptions = (rowData: Tripsheet) => {
    return (
      <div className="align-right">
        {rowData.status === TripsheetStatusEnum.DESBLOQUEADO && (
          <i
            className="pi pi-pencil cursor-pointer"
            title="Editar viagens"
            onClick={() => {
              setTripsheetToAnalyse(rowData);
              setShowModal(true);
            }}
          />
        )}
        {"  "}
        {(rowData.status === TripsheetStatusEnum.DESBLOQUEADO ||
          rowData.status === TripsheetStatusEnum.REJEITADO) && (
            <i
              className="pi pi-lock cursor-pointer"
              title="Bloquear viagens"
              onClick={() => {
                confirmAction("Deseja bloquear este mapa de km?", () => {
                setEmployeeIdToCheckTimesheets(rowData.employee.id);
                setMonthToCheckTimesheets(rowData.month);
                setYearToCheckTimesheets(rowData.year);
                setPendingTripsheet(rowData);
                });
              }}
            />
          )}
        {(rowData.status === TripsheetStatusEnum.BLOQUEADO ||
          rowData.status === TripsheetStatusEnum.A_ASSINAR) && (
            <i
              className="pi pi-lock-open cursor-pointer"
              title="Desbloquear viagens"
              onClick={() => {
                confirmAction("Deseja desbloquear este mapa de km?", () => {
                  rowData.status = TripsheetStatusEnum.DESBLOQUEADO;
                  updateTripsheetMutation(rowData);
                });
              }}
            />
          )}
        {rowData.status === TripsheetStatusEnum.BLOQUEADO &&
          rowData.kmValueCalculated > 0 && (
            <i
              className="pi pi-send cursor-pointer"
              title="Enviar documento para assinatura"
              onClick={() => {
                confirmAction("Deseja enviar para assinatura?", () => {
                  handlerToSign(rowData);
                });
              }}
            />
          )}
        {"  "}
        {(rowData.status === TripsheetStatusEnum.BLOQUEADO ||
          rowData.status === TripsheetStatusEnum.DESBLOQUEADO ||
          rowData.status === TripsheetStatusEnum.REJEITADO ||
          rowData.status === TripsheetStatusEnum.A_ASSINAR ||
          rowData.status === TripsheetStatusEnum.CANCELADO_PRE_ASSINATURA) && (
            <i
              className="pi pi-file-pdf cursor-pointer"
              title="Gerar relatório previsto"
              onClick={() => {
                setAction(ActionTypeEnum.REPORT);

                if (rowData.id === tripsheetIdToReport) {
                  refetchReportData();
                } else if (rowData.id) {
                  setTripsheetIdToReport(rowData.id);
                }
              }}
            />
          )}

        {(rowData.status === TripsheetStatusEnum.ASSINADO_1 ||
          rowData.status === TripsheetStatusEnum.ASSINADO_2 ||
          rowData.status === TripsheetStatusEnum.CANCELADO_POS_ASSINATURA) && (
            <i
              className="pi pi-file-pdf cursor-pointer"
              title="Exibir relatório aprovado"
              onClick={() => {
                if (rowData.id) {
                  setTripsheetIdToReport(rowData.id);
                  console.log("Tripsheet ID to Report:", tripsheetIdToReport);
                  setAction(ActionTypeEnum.SIGN);
                }
              }}
            />
          )}
      </div>
    );
  };

  // Exibe PDF assinado
  const getSignedTripsheet = async (data: DigitalSignApprover[]) => {
    if (data) {
      const digitalSign = data.filter(
        (item) => item.digitalSign.referenceId === tripsheetIdToReport
      );

      const documentApprovedEmployee = digitalSign?.find(
        (item) => item.step === DigitalSignStepEnum.STEP_EMPLOYEE
      );

      let s3Path: string | undefined = "";
      if (documentApprovedEmployee?.documentPath) {
        s3Path = documentApprovedEmployee?.documentPath;
      } else {
        const documentApprovedNimber = digitalSign.find(
          (item) => item.step === DigitalSignStepEnum.STEP_COMPANY
        );
        s3Path = documentApprovedNimber?.documentPath;
      }
      if (s3Path) setS3PathToDisplay(s3Path);

      // if (s3Path) {
      //   const signedUrlResponse = await axios.get(
      //     `${process.env.REACT_APP_BASE_URL}/assets/${s3Path}`
      //   );

      //   const signedUrl = signedUrlResponse.data.url;
      //   if (signedUrl) {
      //     window.open(signedUrl);
      //     setTripsheetIdToReport("");
      //   } else {
      //     showMessage(
      //       MessageTypeEnum.ERROR,
      //       "Relatório",
      //       "Não foi possível obter URL do relatório."
      //     );
      //   }
      // }
    }
  };

  return (
    <div>
      <DataTable
        value={tripsheetsData?.data || []}
        lazy
        paginator
        first={(lazyParams.page - 1) * lazyParams.limit}
        rows={lazyParams.limit}
        totalRecords={tripsheetsData?.pagination?.count || 0}
        loading={isLoading}
        onPage={(e: DataTablePageEvent) =>
          setLazyParams({
            ...lazyParams,
            page: e.page ? e.page + 1 : 1,
            limit: e.rows,
          })
        }
        sortField={lazyParams.sortBy}
        sortOrder={lazyParams.sortOrder === "asc" ? 1 : -1}
        onSort={(e: DataTableSortEvent) => {
          setLazyParams({
            ...lazyParams,
            sortBy: e.sortField,
            sortOrder:
              e.sortOrder === -1 ? "desc" : e.sortOrder === 1 ? "asc" : "",
          });
        }}
        onFilter={(e: DataTableFilterEvent) => setFilters(e.filters)}
        filters={filters}
        size="small"
        resizableColumns
        columnResizeMode="expand"
        header={<HeaderTripMonthEmployeeGrid />}
        rowsPerPageOptions={[2, 10, 25, 50]}
        paginatorLeft
        dataKey="employee.employeeNumber"
        globalFilterFields={[
          "employee.employeeNumber",
          "employee.name, kmValueCalculated",
          "status",
        ]}
      >
        <Column
          field="employee.employeeNumber"
          header={translateText("Number", "pt")}
          sortable
          filter
          filterField="employee.employeeNumber"
          showFilterMatchModes={false}
        />
        <Column
          field="employee.name"
          header={translateText("Name", "pt")}
          sortable
          filter
          filterField="employee.name"
          showFilterMatchModes={false}
        // body={photo}
        // filterField="name"
        // showFilterMatchModes={false}
        />
        <Column
          field="kmValueCalculated"
          header={translateText("Monthly amount", "pt")}
          sortable
          filter
          filterField="kmValueCalculated"
          showFilterMatchModes={false}
          body={(rowData) => {
            const kmValue = rowData.kmValueCalculated ?? 0;
            const kmSupport = rowData.supportTripsValue ?? 0;
            const total = kmValue + kmSupport;
            return total !== 0
              ? total.toLocaleString("pt-PT", {
                style: "currency",
                currency: "EUR", // ou "BRL" para reais
              })
              : "-";

          }
          }
        />
        <Column
          field="version"
          header={translateText("Version", "pt")}
          sortable
        />
        <Column
          field="status"
          header={translateText("Status", "pt")}
          sortable
          filter
          filterField="status"
          showFilterMatchModes={false}
          filterElement={(options) => (
            <StatusTripsheetDropdown options={options} />
          )}
        />
        <Column
          body={showActionOptions}
          header={translateText("Actions", "pt")}
          align="center"
          alignHeader="center"
        />
      </DataTable>
      <Dialog
        header="Editar viagens"
        visible={showModal}
        style={{ width: "80vw" }}
        onHide={handleCloseModal}
      >
        <TripsheetMonthForm
          isDisable={false}
          tripsheetToAnalyse={tripsheetToAnalyse}
        />
      </Dialog>
      <Toast ref={toast} position="bottom-right" />
      {s3PathToDisplay && (
        <PdfViewerDialog
          s3Path={s3PathToDisplay}
          onHide={() => setS3PathToDisplay(null)}
        />
      )}
      <ConfirmDialog />
    </div>
  );
};

export default TripsheetMonthGrid;
